import { Stack, Typography } from "@vapor/react-material";
import type { TFunction } from "i18next";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast, type ToastContent } from "react-toastify";

// Error contract of the government-agency-hub BFF (>= 1.1.15): the toast
// title and message arrive top-level, already translated and UX-approved.
export type BffError = {
    code?: number;
    status?: string;
    title?: string;
    message?: string;
};

type ErrorToast = {
    title?: string;
    message: string;
};

const translateError = async (
    response: Response,
    t: TFunction,
    resourceName?: string,
    fallbackMessage?: string,
): Promise<ErrorToast> => {
    const _response = (await response
        .json()
        .catch(() => null)) as BffError | null;

    if (_response?.message) {
        return { title: _response.title, message: _response.message };
    }

    if (fallbackMessage) {
        return { message: fallbackMessage };
    }

    const status = response.status;
    switch (status) {
        case 401:
            return { message: t("errors.error_notifications.401") };
        case 403:
            return { message: t("errors.error_notifications.403") };
        case 404:
            return { message: t("errors.error_notifications.404") };
        case 504:
            return { message: t("errors.error_notifications.504") };
    }

    if (resourceName) {
        return {
            message: t("errors.error_notifications.default_with_resource", {
                resourceName,
            }),
        };
    }

    return { message: t("errors.error_notifications.default") };
};

const toToastContent = ({ title, message }: ErrorToast): ToastContent => {
    if (!title) {
        return message;
    }
    return (
        <Stack>
            <Typography variant="body700">{title}</Typography>
            <Typography variant="body">{message}</Typography>
        </Stack>
    );
};

export const useErrorNotification = () => {
    const { t } = useTranslation();

    const showTextMessage = useCallback(
        (message?: string, resourceName?: string) => {
            const finalMessage =
                message ??
                (resourceName
                    ? t("errors.error_notifications.default_with_resource", {
                          resourceName,
                      })
                    : t("errors.error_notifications.default"));

            toast.error(finalMessage, {
                position: "top-right",
                hideProgressBar: true,
            });
        },
        [t],
    );

    const showFetchErrorMessage = useCallback(
        async (
            response: Response,
            resourceName?: string,
            fallbackMessage?: string,
        ) => {
            const errorToast = await translateError(
                response,
                t,
                resourceName,
                fallbackMessage,
            );
            toast.error(toToastContent(errorToast), {
                position: "top-right",
                hideProgressBar: true,
            });
        },
        [t],
    );

    return { showTextMessage, showFetchErrorMessage };
};
